import type { ReactElement } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { BodyMuted } from '@/components/shared/typography/BodyText';
import {
  CatalogFramework,
  CatalogId,
  catalogFrameworkLabel,
} from '@kirin/api-client/contracts/ai-instruction-catalog';
import { useAiInstructionCatalog } from '@/hooks/api/useAiInstructionCatalog';
import { policySubPath, AI_AGENT_INSTRUCTIONS_SLUG } from '@/lib/policyPaths';

/**
 * KIR-3816: expanded body of the "AI Agent Instructions" policy card (Policies
 * page). Shows the single-framework chip with its category count and the
 * "Manage catalog" link into the AI Agent Instructions catalog. Scope is derived
 * from the route: `:organizationId` present → org catalog, else personal.
 */
export function AiInstructionCatalogDetails(): ReactElement {
  const { organizationId } = useParams<{ organizationId: string }>();
  const { data } = useAiInstructionCatalog(
    organizationId,
    CatalogId.AgentInstructions
  );
  const count = data?.counts?.[CatalogFramework.Custom];
  const label = catalogFrameworkLabel(
    CatalogId.AgentInstructions,
    CatalogFramework.Custom
  );

  const managePath = policySubPath(organizationId, AI_AGENT_INSTRUCTIONS_SLUG);

  return (
    <div className="space-y-3 pt-4">
      <BodyMuted>
        Framework in this catalog. Open the catalog to review categories and add
        your own.
      </BodyMuted>
      <div className="flex flex-wrap items-center gap-2">
        {/* KIR-3816: "· N categories" so the count reads as a separate object. */}
        <Badge variant="secondary">
          {label}
          {typeof count === 'number' ? ` · ${count} categories` : ''}
        </Badge>
      </div>
      <Link
        to={managePath}
        className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
      >
        Manage catalog
        <ArrowRight className="size-4" />
      </Link>
    </div>
  );
}
